// Vehicle placeholders — admin one-shot behind the Security dashboard's
// "Fill Vehicle Placeholders" button.
//
// Drivers with no car on file cannot go online or be dispatched (the
// onboarding table's vehicle step checks carType). The fill_vehicle_placeholders
// Cloud Function stamps a placeholder car type (the Economy type by default)
// on every driver without one, leaves the plate empty, flags the profile with
// vehiclePlaceholder, and switches on the approved drivers the placeholder
// makes compliant. The call center then collects the real car through the
// Drivers page filter "placeholder car info"; any real car save mirrors the
// plate onto the profile and the driver leaves that list.
//
// Flow: Dry run (counts + sample + candidateUids, writes nothing) → Apply
// (same inputs only), which the portal drives in batches of BATCH uids —
// Firebase Hosting cuts a function rewrite at 60 s, so one request for all
// drivers is never sent. Each batch reads only its own profiles. A batch that
// fails stops the loop; the batches already applied stand (the job is
// idempotent: a re-run dry run shows what is left). After Apply the users
// cache is patched in bulk so the Drivers page reflects the change without
// re-downloading /users.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { api } from 'common';
import {
  Box,
  LinearProgress,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import { confirmDialog, InlineMessage } from '../../components/feedback';
import { FieldLabel, IconCircle } from '../../components/modern';
import { tokens as c } from '../../theme';

const BLOCKER_LABEL = {
  licenseImage: 'licence photo',
  term: 'terms not accepted',
  verifyIdImage: 'ID photo',
  tin: 'TIN',
  carApproved: 'car approval',
  approved: 'account approval',
};

const REASON_LABEL = {
  active: 'already online',
  unapproved: 'unapproved — stamped only',
  in_trip: 'in a trip — stamped only',
};

const reasonText = (r) => {
  if (!r) return 'switch on now';
  if (r.indexOf('blocked:') === 0) return 'still blocked: ' + (BLOCKER_LABEL[r.slice(8)] || r.slice(8));
  return REASON_LABEL[r] || r;
};

const cellSx = { py: 0.75, fontVariantNumeric: 'tabular-nums' };
const headSx = { ...cellSx, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontWeight: 600, fontSize: 12.5 };

// Drivers per apply request: ~15 s of reads, credits, texts and one
// multi-path update — comfortably under Hosting's 60 s rewrite limit.
const BATCH = 100;
const SUM_KEYS = ['stamped', 'activated', 'approved', 'termsStamped', 'enrolled', 'credited', 'smsSent', 'smsFailed', 'smsSkipped',
  'alreadyActive', 'unapproved', 'inTrip', 'belowBalance', 'smsRecent', 'smsAlready', 'carsCreated', 'carsReused', 'carsExisting', 'carsRowGone'];
const LIST_KEYS = ['stampedUids', 'activatedUids', 'approvedUids', 'termUids', 'enrolledUids', 'creditedUids'];

// Default reminder text (Amharic — owner decision, deliberately generic: no
// amount, no day count): "You have received a free daily package gift from
// Donkey Drive! Download or update the app right now, receive trips and earn
// more income. {link}. For info: 9260". Same phrasing as the holiday SMS.
// {link} becomes the Play Store / App Store link per driver.
const DEFAULT_SMS =
  'ከDonkey Drive ነጻ የዕለት ፓኬጅ ስጦታ ተበርክቶሎታል! አሁኑኑ መተግበሪያውን ያውርዱ ወይም ያዘምኑ፣ ጉዞዎችን ይቀበሉ እና የበለጠ ገቢ ያግኙ።\n{link}\nመረጃ ለማግኘት፡ 9260';

export default function VehiclePlaceholderDialog({ open, onClose, onDone }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = useSelector((s) => s.settingsdata.settings) || {};
  const cartypes = useSelector((s) => s.cartypes.cars);
  const names = useMemo(
    () => (Array.isArray(cartypes) ? cartypes.map((c) => c && c.name).filter(Boolean) : []),
    [cartypes]
  );
  const [carType, setCarType] = useState(settings.EconomyCarType || '');
  // All four extras are on by default (owner decisions, 2026-09-13) and stay
  // visible so the dry run shows their counts and any can be unticked:
  // approval is what the Approve switch writes; terms acceptance is recorded
  // on the driver's behalf; flat-rate enrolment follows the midnight reset's
  // rule (never-set mode, non-fleet, app >= 2.0.56); the holiday credit goes
  // through the once-per-phone ledger (no SMS / push from this job).
  const [approve, setApprove] = useState(true);
  const [acceptTerms, setAcceptTerms] = useState(true);
  const [enrollFlatRate, setEnrollFlatRate] = useState(true);
  const [holidayCredit, setHolidayCredit] = useState(true);
  // Reminder SMS (Amharic by default, editable; {link} = store link by platform).
  const [sendSms, setSendSms] = useState(true);
  const [smsMessage, setSmsMessage] = useState(settings.appUpdateReminderMessage || DEFAULT_SMS);
  const creditAmount = settings.holidayPromoAmount === undefined || settings.holidayPromoAmount === null || settings.holidayPromoAmount === ''
    ? 10 : Number(settings.holidayPromoAmount) || 0;
  const [limit, setLimit] = useState('');
  const [running, setRunning] = useState(null); // 'dry' | 'apply' | 'cache'
  const [dry, setDry] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null); // { done, total } while applying

  // settings can arrive after the first render
  useEffect(() => {
    if (!carType && settings.EconomyCarType) setCarType(settings.EconomyCarType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.EconomyCarType]);

  const body = { carType, approve, acceptTerms, enrollFlatRate, holidayCredit, sendSms, smsMessage: sendSms ? smsMessage : '', limit: Number(limit) || 0 };
  const inputsKey = JSON.stringify(body);
  const dryMatches = Boolean(dry && dry.inputsKey === inputsKey);
  const busy = running !== null;

  const handleDryRun = async () => {
    setRunning('dry');
    setError(null);
    try {
      const r = await api.postWithIdToken('/fill_vehicle_placeholders', { ...body, dryRun: true });
      setDry({ ...r, inputsKey });
    } catch (e) {
      setError((e.payload && e.payload.message) || e.message);
    } finally {
      setRunning(null);
    }
  };

  const handleApply = async () => {
    if (!dryMatches) return;
    const ok = await confirmDialog({
      title: t('placeholder_apply_confirm', { drivers: dry.selected, defaultValue: 'Process {{drivers}} driver(s)?' }),
      body: (
        <>
          <Typography variant="body1" sx={{ color: c.INK_2, mb: 1.5 }}>
            {t('placeholder_apply_confirm_steps', {
              fresh: dry.fresh,
              repair: dry.repair,
              carType,
              approve: dry.approveCount,
              terms: dry.termsCount,
              enroll: dry.enrollCount,
              credit: dry.creditCount,
              amount: dry.creditAmount,
              sms: dry.smsCount,
              activate: dry.activate,
              defaultValue: '{{fresh}} without a car type and {{repair}} stamped earlier that need their car row: stamp car type "{{carType}}", add or reuse a /cars row, approve {{approve}}, mark terms accepted for {{terms}}, enrol {{enroll}} in the daily flat rate, credit {{credit}} with {{amount}} ETB, text {{sms}} of them the app reminder, and switch on {{activate}} of them now.',
            })}
          </Typography>
          <Typography variant="body1" sx={{ color: c.INK_2 }}>
            {t('placeholder_apply_confirm_note', { defaultValue: 'The plate stays empty (customers see "Plate: N/A"). The call center collects the real car from Drivers → Car info: Placeholder. This cannot be undone in bulk.' })}
          </Typography>
        </>
      ),
      confirmLabel: t('placeholder_apply_confirm_button', { defaultValue: 'Process drivers' }),
      destructive: true,
    });
    if (!ok) return;
    setRunning('apply');
    setError(null);
    const uids = dry.candidateUids || [];
    // Aggregate of every batch response (same keys as a single response).
    const r = { carType: dry.carType, creditAmount: dry.creditAmount, sendSms: dry.sendSms, errors: [], creditSkipped: {}, elapsedMs: 0 };
    SUM_KEYS.forEach((k) => { r[k] = 0; });
    LIST_KEYS.forEach((k) => { r[k] = []; });
    let failed = null;
    setProgress({ done: 0, total: uids.length });
    for (let i = 0; i < uids.length; i += BATCH) {
      const slice = uids.slice(i, i + BATCH);
      try {
        // eslint-disable-next-line no-await-in-loop
        const b = await api.postWithIdToken('/fill_vehicle_placeholders', { ...body, dryRun: false, uids: slice });
        SUM_KEYS.forEach((k) => { r[k] += Number(b[k]) || 0; });
        LIST_KEYS.forEach((k) => { r[k] = r[k].concat(b[k] || []); });
        Object.keys(b.creditSkipped || {}).forEach((k) => { r.creditSkipped[k] = (r.creditSkipped[k] || 0) + b.creditSkipped[k]; });
        r.errors = r.errors.concat(b.errors || []);
        r.elapsedMs += Number(b.elapsedMs) || 0;
        ['patch', 'activatedPatch', 'approvedPatch', 'termPatch', 'enrolledPatch', 'creditedPatch'].forEach((k) => { if (b[k]) r[k] = b[k]; });
        setProgress({ done: Math.min(i + slice.length, uids.length), total: uids.length });
      } catch (e) {
        failed = `batch ${Math.floor(i / BATCH) + 1} of ${Math.ceil(uids.length / BATCH)} failed: ${(e.payload && e.payload.message) || e.message}`;
        break;
      }
    }
    try {
      setRunning('cache');
      const patches = {};
      (r.stampedUids || []).forEach((uid) => { patches[uid] = { ...(r.patch || {}) }; });
      (r.activatedUids || []).forEach((uid) => { patches[uid] = { ...(patches[uid] || {}), ...(r.activatedPatch || {}) }; });
      (r.approvedUids || []).forEach((uid) => { patches[uid] = { ...(patches[uid] || {}), ...(r.approvedPatch || {}) }; });
      (r.termUids || []).forEach((uid) => { patches[uid] = { ...(patches[uid] || {}), ...(r.termPatch || {}) }; });
      (r.enrolledUids || []).forEach((uid) => { patches[uid] = { ...(patches[uid] || {}), ...(r.enrolledPatch || {}) }; });
      (r.creditedUids || []).forEach((uid) => { patches[uid] = { ...(patches[uid] || {}), ...(r.creditedPatch || {}) }; });
      await api.patchUsersCacheBulk(patches);
      const parts = [
        `Updated ${r.stamped} driver(s) with car type "${r.carType}": ${r.carsCreated} car rows created, ${r.carsReused} existing rows reused, ${r.carsExisting} already fine${r.carsRowGone ? `, ${r.carsRowGone} placeholder rows deleted by an agent (left alone)` : ''}; switched on ${r.activated}`,
        r.approved ? `approved ${r.approved}` : null,
        r.termsStamped ? `terms marked accepted for ${r.termsStamped}` : null,
        r.enrolled ? `${r.enrolled} enrolled in the daily flat rate` : null,
        r.credited ? `${r.credited} credited ${r.creditAmount} ETB` : null,
        r.sendSms ? `SMS sent ${r.smsSent}${r.smsFailed ? `, failed ${r.smsFailed}` : ''}${r.smsSkipped ? `, stopped ${r.smsSkipped} (SMS outage)` : ''}${r.smsAlready ? `, ${r.smsAlready} texted earlier` : ''}` : null,
        r.creditSkipped && Object.keys(r.creditSkipped).length
          ? `credit skipped: ${Object.keys(r.creditSkipped).map((k) => `${k} ${r.creditSkipped[k]}`).join(', ')}` : null,
        r.alreadyActive ? `${r.alreadyActive} were already online (now dispatchable)` : null,
        r.unapproved ? `${r.unapproved} unapproved (stamped only)` : null,
        r.belowBalance ? `${r.belowBalance} switched on but below one day's flat-rate balance (held out of dispatch until they top up)` : null,
      ].filter(Boolean);
      const message = `${parts.join(', ')} in ${Math.round((r.elapsedMs || 0) / 1000)} s.`;
      if (failed) {
        onDone({ error: `${message} Stopped: ${failed}. Run a new dry run to see what is left — batches already applied stand.` });
      } else if (r.errors && r.errors.length) {
        onDone({ error: `${message} ${r.errors.length} error(s): ${r.errors[0]}` });
      } else {
        onDone({ success: true, message });
      }
      setDry(null);
      onClose();
    } catch (e) {
      setError((e.payload && e.payload.message) || e.message);
    } finally {
      setRunning(null);
      setProgress(null);
    }
  };

  const handleClose = () => {
    if (busy) return;
    setError(null);
    onClose();
  };

  const blocked = dry ? Object.keys(dry.stillBlocked || {}) : [];
  const rows = dry ? [
    ['Drivers scanned', dry.totalDrivers],
    ['Candidates', dry.candidates],
    ['— without a car type (fresh)', dry.fresh],
    ['— stamped earlier, car row to add (repair: the /cars row is checked per driver on apply; nothing already done is redone)', dry.repair],
    ['Selected for this run', dry.selected + (dry.limit ? ` (limit ${dry.limit})` : '')],
    ['Will be approved (approved + adminApprovedTrue)', dry.approveCount],
    ['Terms marked accepted on their behalf', dry.termsCount],
    ['Enrolled in the daily flat rate', dry.enrollFlatRate ? dry.enrollCount : (dry.flatRateEnabled ? 'off' : 'flat rate disabled in Settings')],
    ['Not enrolled — app older than 2.0.56 / unreported (the midnight reset enrols them when they update)', dry.enrollSkippedOldApp],
    ['Not enrolled — explicitly per-trip / fleet', (dry.enrollSkippedExplicit || 0) + (dry.enrollSkippedFleet || 0)],
    [`Holiday credit granted (${dry.creditAmount} ETB, no SMS / push)`, dry.holidayCredit ? dry.creditCount : 'off'],
    ['Already holds a credit (welcome / holiday)', dry.creditAlready],
    ['Reminder SMS to send (Android / iOS / unknown → Android link)', dry.sendSms
      ? `${dry.smsCount} (${(dry.smsPlatform || {}).ANDROID || 0} / ${(dry.smsPlatform || {}).IOS || 0} / ${(dry.smsPlatform || {}).unknown || 0})`
      : 'off'],
    ['No phone number — no SMS', dry.sendSms ? dry.smsNoPhone : 'off'],
    ['Texted in the last 24 h — no SMS again', dry.sendSms ? dry.smsRecent : 'off'],
    ['Texted by the earlier run — no SMS again', dry.sendSms ? dry.smsAlready : 'off'],
    ['Will be switched on now', dry.activate],
    ['Already online — become dispatchable', dry.alreadyActive],
    ['Unapproved — stamped, not switched on', dry.unapproved],
    ['In a trip — stamped, not switched on', dry.inTrip],
    ['Still blocked after the placeholder', blocked.length
      ? blocked.map((k) => `${BLOCKER_LABEL[k] || k}: ${dry.stillBlocked[k]}`).join(', ')
      : 0],
    ['Switched on but below one day\'s flat-rate balance — dispatch waits for a top-up', dry.belowBalance],
  ] : [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={phone} aria-labelledby="vp-title">
      <DialogTitle id="vp-title" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pr: 8 }}>
        <IconCircle icon={<DirectionsCarIcon />} hue="accent" size={34} />
        Fill Vehicle Placeholders
        <IconButton aria-label={t('close_dialog', { defaultValue: 'Close' })} onClick={handleClose} disabled={busy} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: c.INK_2 }}>
          Every driver with no car type gets this placeholder car type, carApproved, and a matching car row
          (Cars tab / the driver's Cars screen) with the car type's default photo, so they can go online and
          receive bookings. The plate, make and model stay empty. Drivers stamped by an earlier run without a car row
          get their row added. Drivers who are approved (or approved by this run)
          and pass the onboarding steps are switched on immediately. Each stamped driver shows an amber Placeholder chip in the Drivers
          list's Car column and under the Car info: Placeholder filter until an agent saves the real car.
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end" mb={1.5}>
          <Box sx={{ minWidth: 220 }}>
            <FieldLabel htmlFor="vp-cartype">Placeholder car type</FieldLabel>
            <TextField
              id="vp-cartype"
              select
              size="small"
              fullWidth
              value={names.indexOf(carType) >= 0 ? carType : ''}
              onChange={(e) => setCarType(e.target.value)}
              disabled={busy}
            >
              {names.map((n) => (
                <MenuItem key={n} value={n}>{n}{n === settings.EconomyCarType ? ' (Economy setting)' : ''}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ width: 190 }}>
            <FieldLabel htmlFor="vp-limit">Limit (canary, 0 = all)</FieldLabel>
            <TextField
              id="vp-limit"
              size="small"
              fullWidth
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              disabled={busy}
              inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={0.25} mb={2}>
          <FormControlLabel
            control={<Checkbox checked={approve} onChange={(e) => setApprove(e.target.checked)} disabled={busy} />}
            label="Approve unapproved drivers (what the Approve switch writes)"
          />
          <FormControlLabel
            control={<Checkbox checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} disabled={busy} />}
            label="Mark terms as accepted on their behalf (term: true)"
          />
          <FormControlLabel
            control={<Checkbox checked={enrollFlatRate} onChange={(e) => setEnrollFlatRate(e.target.checked)} disabled={busy || settings.driverFlatRateEnabled !== true} />}
            label={settings.driverFlatRateEnabled === true
              ? 'Enrol in the daily flat rate (never-set mode, non-fleet, app 2.0.56+)'
              : 'Enrol in the daily flat rate (enable driverFlatRateEnabled in Settings first)'}
          />
          <FormControlLabel
            control={<Checkbox checked={holidayCredit} onChange={(e) => setHolidayCredit(e.target.checked)} disabled={busy} />}
            label={`Grant the holiday credit (${creditAmount} ETB top-up) to drivers who hold no credit yet — no SMS / push`}
          />
          <FormControlLabel
            control={<Checkbox checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} disabled={busy} />}
            label="Text every stamped driver an app download / update reminder (Amharic by default; store link by the driver's platform)"
          />
        </Box>
        {sendSms && (
          <Box sx={{ mb: 2 }}>
            <FieldLabel htmlFor="vp-sms">Reminder SMS ({'{link}'} = Play Store / App Store link by platform)</FieldLabel>
            <TextField
              id="vp-sms"
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              disabled={busy}
              multiline
              minRows={3}
              fullWidth
              size="small"
              inputProps={{ 'aria-describedby': 'vp-sms-count' }}
            />
            <Typography id="vp-sms-count" variant="caption" sx={{ display: 'block', mt: 0.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>
              {`${smsMessage.length} characters · Android link: ${settings.PlayStoreLink || 'PlayStoreLink not set'} · iOS link: ${settings.AppleStoreLink || 'AppleStoreLink not set'}`}
            </Typography>
          </Box>
        )}

        {!settings.EconomyCarType && (
          <InlineMessage kind="warning" sx={{ mb: 2 }}>
            settings.EconomyCarType is not set — pick the car type explicitly. Dispatch treats the Economy
            setting and the EV setting as interchangeable; any other type only receives its own bookings.
          </InlineMessage>
        )}
        {acceptTerms && (
          <InlineMessage kind="warning" sx={{ mb: 2 }}>
            This records terms acceptance for drivers who never tapped Accept. The alternative that writes
            nothing per driver is Settings → Onboarding wizard → set the Terms step to Optional, which lifts
            the block for everyone (the wizard still shows the terms to new drivers).
          </InlineMessage>
        )}
        {error && <InlineMessage kind="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</InlineMessage>}
        {progress && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Applying in batches of {BATCH} — {progress.done} / {progress.total} drivers (keep this page open)
            </Typography>
            <LinearProgress variant="determinate" value={progress.total ? Math.round((progress.done / progress.total) * 100) : 0} />
          </Box>
        )}

        {dry && (
          <>
            {dry.vehicleStepMode === 'off' && (
              <InlineMessage kind="info" sx={{ mb: 2 }}>
                The vehicle step is Off in the onboarding table, so a missing car type is not blocking anyone
                today. Stamping only restricts these drivers to "{dry.carType}" bookings.
              </InlineMessage>
            )}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: c.INK }}>
              Dry run — nothing was written
            </Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableBody>
                {rows.map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell sx={cellSx}>{label}</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700 }} align="right">{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {dry.sample && dry.sample.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: c.INK }}>
                  Sample ({dry.sample.length} of {dry.selected})
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Driver</TableCell>
                      <TableCell sx={headSx}>Mobile</TableCell>
                      <TableCell sx={headSx}>Decision</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dry.sample.map((s) => (
                      <TableRow key={s.uid}>
                        <TableCell sx={cellSx}>{s.name || s.uid}</TableCell>
                        <TableCell sx={cellSx}>{settings.AllowCriticalEditsAdmin ? s.mobile : '••••'}</TableCell>
                        <TableCell sx={cellSx}>
                          {reasonText(s.reason)}
                          {s.enroll ? ' · flat rate' : ''}
                          {s.credit ? ` · +${dry.creditAmount} ETB` : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
            {!dryMatches && (
              <InlineMessage kind="warning" sx={{ mt: 2 }}>Inputs changed since this dry run — run it again before applying.</InlineMessage>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="neutral" onClick={handleClose} disabled={busy}>Close</Button>
        <Button
          onClick={handleDryRun}
          variant="outlined"
          color="neutral"
          disabled={busy || !carType}
          startIcon={running === 'dry' ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
        >
          {running === 'dry' ? 'Scanning…' : 'Dry run'}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={busy || !dryMatches || !dry || !(dry.candidateUids && dry.candidateUids.length)}
          startIcon={running === 'apply' || running === 'cache' ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
        >
          {running === 'apply' ? (progress ? `Applying ${progress.done}/${progress.total}…` : 'Applying…') : running === 'cache' ? 'Updating list…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
